import { store } from '@/store'
import { eventComparator } from '@learnics/models/src/Event/utils/eventComparator.mjs'
import { loggerSessionFromJson } from '@learnics/models/src/Submission/loggerSessionFromJson.mjs'
import { transitionCalculators } from '@learnics/models/src/RunningCalculator/utils/transitionCalculators.mjs'
import { eventFromJson } from '@learnics/models/src/Event/utils/eventFromJson.mjs'
import { redact } from '@learnics/models/src/utils/redact.mjs'
import { Assignment } from '@learnics/models/src/Assignment/Assignment.mjs'
import { AssignmentDetail } from '@learnics/models/src/Assignment/AssignmentDetail.mjs'
import debounce from 'lodash/debounce'

let eventCache = {}
let secretsCache = {}

const clearEventCache = debounce(() => {
  const sessionIds = Object.keys(eventCache)
  let newEventCache = {}
  for (const sessionId of sessionIds) {
    const batchIds = Object.keys(eventCache[sessionId])
    const allEvents = []
    for (const batchId of batchIds) {
      allEvents.push(...eventCache[sessionId][batchId])
    }
    allEvents.sort(eventComparator)
    const result = transitionCalcs(sessionId, allEvents)
    if (!result) {
      newEventCache[sessionId] = eventCache[sessionId]
    }
  }
  eventCache = newEventCache
  if (Object.keys(eventCache).length > 0) {
    setTimeout(clearEventCache, 5000) // Check back in 5 seconds, ultimately looping until all events are processed
    clearEventCache()
  }
}, 1000)

/**
 * Attempt to transition the calculators for a given session based on events
 * emitted from the student's extension.
 **/

export function transitionCalcs(sessionId, events) {
  const session = store.getters.getLoggerSessions[sessionId]

  const runningCalculators = store.getters.getRunningCalculators
  const assignments = store.getters.getAssignments
  const assignmentDetails = store.getters.getAssignmentDetails
  const assignment = assignments[session?.assignmentId]
  const assignmentDetail = assignmentDetails[assignment?.assignmentDetailId]
  const keysDeleted = store.getters.getKeysDeleted[sessionId]
  let secrets = store.getters.getSecrets[sessionId] || {}
  if (secretsCache[sessionId]) {
    secrets = { ...secrets, ...secretsCache[sessionId] }
  }

  if (!sessionId || !secrets || !keysDeleted) {
    return false
  }
  let commonCalculators = {}
  if (session?.assignmentId) {
    commonCalculators =
      store.getters.getCommonRunningCalculators[session.assignmentId] || {}
  }
  const stateData = {
    assignment,
    assignmentDetail,
    keysDeleted,
    keysBlacklisted: new Set(),
    secrets,
    sessionId,
    timeRange: {
      start: 0,
      stop: Infinity,
    },
  }
  try {
    for (const event of events) {
      transitionCalculators(
        event,
        stateData,
        runningCalculators[sessionId] || {},
        commonCalculators,
      )
    }
    store.commit('addRunningCalculators', {
      sessionId,
      calculators: { ...runningCalculators[sessionId] },
      common: false,
    })
  } catch (e) {
    return false
  }
  return true
}

export function onStudentLinkStorageUpdate(
  extensionId,
  { key, oldValue, newValue },
) {
  if (key === 'selectedSessionId') {
    store.commit('setExtensionSelectedSessionId', {
      selectedSessionId: newValue,
      extensionId,
    })
  } else if (key === 'selectedAssignmentId') {
    store.commit('setExtensionSelectedAssignmentId', {
      selectedAssignmentId: newValue,
      extensionId,
    })
  } else if (key === 'logging') {
    store.commit('setExtensionLogging', { logging: newValue, extensionId })
  } else if (key === 'initializingLogger') {
    store.commit('setExtensionInitializingLogger', {
      initializingLogger: newValue,
      extensionId,
    })
  } else if (key === 'logger') {
    /** @deprecated - the LoggerEntity no longer exists as of version 4.0.0 of the
     * extensions.  It should be removed from the store in the future, after
     * all extensions have had time to update.
     **/
    store.commit('setExtensionInitializingLogger', {
      initializingLogger: !!newValue?.loading,
      extensionId,
    })
    store.commit('setExtensionSelectedAssignmentId', {
      selectedAssignmentId: newValue?.session?.assignmentId || null,
      extensionId,
    })
    store.commit('setExtensionLogging', {
      logging: !!newValue?.logging,
      extensionId,
    })
    /** end of deprecated code **/
  } else if (key.startsWith('asyncCommandStatus')) {
    const commandType = key.split('/')[1]
    const commandId = key.split('/')[2]
    const payload = {
      compositeId: {
        commandType,
        commandId,
      },
      newVal: newValue,
    }
    store.commit('setAsyncCommandStatus', payload)
  } else if (key.startsWith('loggerSession/')) {
    store.commit('addLoggerSession', loggerSessionFromJson(newValue))
  } else if (key.startsWith('eventBatch/')) {
    const sessionId = key.split('/')[1]
    const batchId = key.split('/')[2]
    const events = (newValue?.events || []).map(eventFromJson)

    for (const event of events) {
      const secrets = event.redact(redact)
      // Redact the event and store the secrets in a cache
      Object.keys(secrets).forEach((key) => {
        secretsCache[sessionId] ||= {}
        secretsCache[sessionId][key] = secrets[key]
      })
    }
    // Store the events in a cache using a debounce, collecting as many events
    // as possible and giving a short delay before attempting to transition
    // the calculators.  This prevents us from transitioning the calculators
    // too early, before all events have been received and can be sorted by
    // timestamp.
    eventCache[sessionId] ||= {}
    eventCache[sessionId][batchId] ||= events

    clearEventCache()
  } else if (key.startsWith('events/')) {
    /** @deprecated - the EventsEntity no longer exists as of version 4.0.0 of the
     * extensions.  It should be removed from the store in the future, after
     * all extensions have had time to update.
     */
    console.log('new events', newValue)

    const sessionId = key.split('/')[1]
    const batchId = key.split('/')[2]
    const events = newValue.map(eventFromJson)

    for (const event of events) {
      const secrets = event.redact(redact)
      // Redact the event and store the secrets in a cache
      Object.keys(secrets).forEach((key) => {
        secretsCache[sessionId] ||= {}
        secretsCache[sessionId][key] = secrets[key]
      })
    }
    // Store the events in a cache using a debounce, collecting as many events
    // as possible and giving a short delay before attempting to transition
    // the calculators.  This prevents us from transitioning the calculators
    // too early, before all events have been received and can be sorted by
    // timestamp.
    eventCache[sessionId] ||= {}
    eventCache[sessionId][batchId] ||= events

    clearEventCache()
    /** end of deprecated code **/
  } else if (key.startsWith('secrets/')) {
    const sessionId = key.split('/')[1]
    const payload = {
      sessionId,
      secrets: newValue,
    }
    store.commit('addSecrets', payload)
  } else if (key.startsWith('keysDeleted/')) {
    const sessionId = key.split('/')[1]
    const payload = {
      sessionId,
      keysDeleted: newValue,
    }
    store.commit('addKeysDeleted', payload)
  } else if (key.startsWith('assignment/')) {
    if (newValue) {
      store.commit('addAssignment', new Assignment(newValue))
    }
  } else if (key.startsWith('assignmentDetail/')) {
    const assignmentDetail = new AssignmentDetail(newValue)
    const existingAssignmentDetail =
      store.getters.getAssignmentDetails[newValue.id]
    if (
      !existingAssignmentDetail ||
      existingAssignmentDetail.updated < assignmentDetail.updated
    ) {
      store.commit('addAssignmentDetail', assignmentDetail)
    } // Otherwise ignore the update
  }
}
